import { useState } from 'react';
import { Button, Stack, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Alert, IconButton, Tooltip, Chip } from '@mui/material';
import { Add, PlayArrow, Autorenew } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { contractsApi, type AmcContract } from '../../api/amc.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const COVERAGE = ['comprehensive', 'non_comprehensive', 'labour_only', 'spare_inclusive'];
const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;

export function ContractsPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [open, setOpen] = useState(false);
  const [renewing, setRenewing] = useState<AmcContract | null>(null);
  const [form, setForm] = useState({ clientId: '', title: '', startDate: '', endDate: '', contractValue: '', coverageType: 'comprehensive', assetIds: '' });
  const [renewForm, setRenewForm] = useState({ endDate: '', contractValue: '' });
  const [error, setError] = useState<string | null>(null);

  const list = useQuery({ queryKey: ['amc-contracts', page, limit], queryFn: () => contractsApi.list({ page, limit }) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['amc-contracts'] });

  const save = useMutation({
    mutationFn: () => contractsApi.create({
      clientId: Number(form.clientId), title: form.title, startDate: form.startDate, endDate: form.endDate,
      contractValue: form.contractValue ? Number(form.contractValue) : undefined, coverageType: form.coverageType,
      assetIds: form.assetIds ? form.assetIds.split(',').map((s) => Number(s.trim())).filter(Boolean) : undefined,
    }),
    onSuccess: () => { setOpen(false); invalidate(); }, onError: (e) => setError(extractApiError(e)),
  });
  const activate = useMutation({ mutationFn: (id: number) => contractsApi.changeStatus(id, 'activate'), onSuccess: invalidate });
  const renew = useMutation({
    mutationFn: () => contractsApi.renew(renewing!.id, { endDate: renewForm.endDate, contractValue: renewForm.contractValue ? Number(renewForm.contractValue) : undefined }),
    onSuccess: () => { setRenewing(null); invalidate(); }, onError: (e) => setError(extractApiError(e)),
  });

  const columns: Column<AmcContract>[] = [
    { key: 'code', label: 'Code', width: 150 },
    { key: 'title', label: 'Title' },
    { key: 'coverageType', label: 'Coverage', render: (r) => <Chip size="small" label={r.coverageType.replace(/_/g, ' ')} variant="outlined" /> },
    { key: 'endDate', label: 'End', render: (r) => `${r.endDate}${r.daysToExpiry != null ? ` (${r.daysToExpiry}d)` : ''}` },
    { key: 'contractValue', label: 'Value', align: 'right', render: (r) => inr(r.contractValue) },
    { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
  ];

  return (
    <>
      <PageHeader title="AMC Contracts" subtitle="Annual / multi-year service contracts, SLA matrix, coverage and renewals." action={hasPermission('amc_contract.create') && <Button startIcon={<Add />} variant="contained" onClick={() => { setError(null); setOpen(true); }}>New Contract</Button>} />

      <DataTable columns={columns} rows={list.data?.data ?? []} loading={list.isLoading} page={page} limit={limit} total={list.data?.meta.total ?? 0} onPageChange={setPage} onLimitChange={setLimit} rowKey={(r) => r.id} emptyTitle="No contracts"
        actions={(r) => (
          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
            {r.status === 'draft' && hasPermission('amc_contract.approve') && <Tooltip title="Activate"><IconButton size="small" color="success" onClick={() => activate.mutate(r.id)}><PlayArrow fontSize="small" /></IconButton></Tooltip>}
            {['active', 'expired'].includes(r.status) && hasPermission('amc_contract.approve') && <Tooltip title="Renew"><IconButton size="small" onClick={() => { setRenewing(r); setRenewForm({ endDate: '', contractValue: String(r.contractValue) }); setError(null); }}><Autorenew fontSize="small" /></IconButton></Tooltip>}
          </Stack>
        )}
      />

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>New AMC Contract</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <Stack direction="row" spacing={2}>
              <TextField label="Client ID" type="number" value={form.clientId} onChange={(e) => setForm((f) => ({ ...f, clientId: e.target.value }))} fullWidth />
              <TextField select label="Coverage" value={form.coverageType} onChange={(e) => setForm((f) => ({ ...f, coverageType: e.target.value }))} fullWidth>
                {COVERAGE.map((c) => <MenuItem key={c} value={c}>{c.replace(/_/g, ' ')}</MenuItem>)}
              </TextField>
            </Stack>
            <TextField label="Title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} fullWidth />
            <Stack direction="row" spacing={2}>
              <TextField label="Start" type="date" InputLabelProps={{ shrink: true }} value={form.startDate} onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))} fullWidth />
              <TextField label="End" type="date" InputLabelProps={{ shrink: true }} value={form.endDate} onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))} fullWidth />
              <TextField label="Value" type="number" value={form.contractValue} onChange={(e) => setForm((f) => ({ ...f, contractValue: e.target.value }))} sx={{ width: 160 }} />
            </Stack>
            <TextField label="Asset IDs (comma separated)" value={form.assetIds} onChange={(e) => setForm((f) => ({ ...f, assetIds: e.target.value }))} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setOpen(false)}>Cancel</Button><Button variant="contained" disabled={!form.clientId || !form.title || !form.startDate || !form.endDate} onClick={() => save.mutate()}>Create</Button></DialogActions>
      </Dialog>

      <Dialog open={Boolean(renewing)} onClose={() => setRenewing(null)} fullWidth maxWidth="xs">
        <DialogTitle>Renew {renewing?.code}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {error && <Alert severity="error">{error}</Alert>}
            <TextField label="New End Date" type="date" InputLabelProps={{ shrink: true }} value={renewForm.endDate} onChange={(e) => setRenewForm((f) => ({ ...f, endDate: e.target.value }))} fullWidth />
            <TextField label="Contract Value" type="number" value={renewForm.contractValue} onChange={(e) => setRenewForm((f) => ({ ...f, contractValue: e.target.value }))} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions><Button onClick={() => setRenewing(null)}>Cancel</Button><Button variant="contained" disabled={!renewForm.endDate} onClick={() => renew.mutate()}>Renew</Button></DialogActions>
      </Dialog>
    </>
  );
}
